import { FileText } from 'lucide-react';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';
import { Section } from './Section.js';
import { ArtifactItem } from './ArtifactItem.js';
import { useAppContext } from '@/contexts/AppContext.js';
import { useEffect } from 'react';

export function NotesSection() {
  const sections = useArtifactPanelStore((state) => state.sections);
  const toggleSection = useArtifactPanelStore((state) => state.toggleSection);
  const getArtifactsByType = useArtifactPanelStore((state) => state.getArtifactsByType);
  const registerArtifact = useArtifactPanelStore((state) => state.registerArtifact);
  const openTab = useArtifactPanelStore((state) => state.openTab);
  const togglePin = useArtifactPanelStore((state) => state.togglePin);
  const deleteArtifact = useArtifactPanelStore((state) => state.deleteArtifact);
  const activeTabId = useArtifactPanelStore((state) => state.activeTabId);
  const openTabs = useArtifactPanelStore((state) => state.openTabs);
  const searchQuery = useArtifactPanelStore((state) => state.searchQuery);

  const { notes, deleteNote, createNewNote, currentNote } = useAppContext();

  // Sync notes from AppContext to artifact store
  useEffect(() => {
    notes.forEach((note) => {
      const artifactId = `note-${note.id}`;
      registerArtifact({
        id: artifactId,
        type: 'note',
        title: note.title || 'Untitled Note',
        isPinned: false,
        lastOpened: note.updatedAt,
        createdAt: note.createdAt,
        modifiedAt: note.updatedAt,
        data: { noteId: note.id },
      });
    });
  }, [notes, registerArtifact]);

  // Function to create a new note
  const handleCreateNote = () => {
    // Create note in AppContext (this creates the actual note and sets it as currentNote)
    createNewNote();

    // The currentNote will be updated after the above call
    // We need to wait for the next render to get the new note ID
    // Use setTimeout to ensure state has updated
    setTimeout(() => {
      if (currentNote) {
        const artifactId = `note-${currentNote.id}`;

        // Register as artifact
        registerArtifact({
          id: artifactId,
          type: 'note',
          title: currentNote.title || 'Untitled Note',
          isPinned: false,
          lastOpened: Date.now(),
          createdAt: currentNote.createdAt,
          modifiedAt: currentNote.updatedAt,
          data: { noteId: currentNote.id },
        });

        // Open as tab
        openTab({
          id: artifactId,
          type: 'note',
          title: currentNote.title || 'Untitled Note',
          data: { noteId: currentNote.id },
        });
      }
    }, 0);
  };

  // Function to handle note deletion
  const handleDeleteNote = (artifactId: string) => {
    // Extract the note ID from the artifact ID (format: "note-{noteId}")
    const noteId = artifactId.replace('note-', '');

    // Delete from AppContext (actual note data)
    deleteNote(noteId);

    // Delete from artifact store
    deleteArtifact(artifactId);
  };

  let noteArtifacts = getArtifactsByType('note');

  // Filter by search query
  if (searchQuery) {
    noteArtifacts = noteArtifacts.filter((artifact) =>
      artifact.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Sort notes
  const sortBy = sections.notes.sortBy;
  noteArtifacts = [...noteArtifacts].sort((a, b) => {
    if (sortBy === 'modified') {
      return b.modifiedAt - a.modifiedAt;
    } else if (sortBy === 'created') {
      return b.createdAt - a.createdAt;
    } else {
      return a.title.localeCompare(b.title);
    }
  });

  return (
    <Section
      title="Notes"
      icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
      count={noteArtifacts.length}
      collapsed={sections.notes.collapsed}
      onToggle={() => toggleSection('notes')}
      onNew={handleCreateNote}
    >
      {noteArtifacts.length === 0 ? (
        <div className="px-3 py-2 text-xs text-muted-foreground">
          No notes yet. Click + to create one.
        </div>
      ) : (
        noteArtifacts.map((artifact) => (
          <ArtifactItem
            key={artifact.id}
            artifact={artifact}
            isActive={activeTabId === artifact.id}
            isOpen={openTabs.includes(artifact.id)}
            onClick={() => openTab({ id: artifact.id, type: artifact.type, title: artifact.title, data: artifact.data })}
            onPin={() => togglePin(artifact.id)}
            onDelete={() => handleDeleteNote(artifact.id)}
          />
        ))
      )}
    </Section>
  );
}
